// frontend/src/components/PhotoUpload.js
import React, { useState, useCallback, useRef } from 'react';
import Webcam from 'react-webcam';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import { supabase } from '../services/supabaseClient';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import toast from 'react-hot-toast';

// Image quality requirements
const IMAGE_REQUIREMENTS = {
  minWidth: 800,
  minHeight: 800,
  maxFileSize: 5 * 1024 * 1024, // 5MB
  acceptedFormats: ['image/jpeg', 'image/png', 'image/webp'],
  compressionOptions: {
    maxSizeMB: 2,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    initialQuality: 0.9
  }
};

const PhotoUpload = ({ player, eventCode, onUploadComplete }) => {
  const currentEventCode = eventCode || 'mjwc2025'; // Default fallback
  const [currentStep, setCurrentStep] = useState('select'); // select, capture, crop, preview
  const [mode, setMode] = useState(null); // camera or upload
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [croppedBlob, setCroppedBlob] = useState(null); // Actual blob for upload
  const [crop, setCrop] = useState({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10,
    aspect: 1 // Square aspect ratio
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [facingMode, setFacingMode] = useState("user");
  const [isMirrored, setIsMirrored] = useState(true);
  const [cameraError, setCameraError] = useState(false);
  // const [showSuccessModal, setShowSuccessModal] = useState(false); // Unused
  const hasExistingPhoto = player?.upload_status || player?.photo_url;

  const webcamRef = useRef(null);
  const imageRef = useRef(null);
  const previewCanvasRef = useRef(null);


  // Process and compress image
  const processImage = useCallback(async (file) => {
    setIsProcessing(true);
    try {
      const compressedFile = await imageCompression(file, IMAGE_REQUIREMENTS.compressionOptions);
      const imageUrl = URL.createObjectURL(compressedFile);
      return { file: compressedFile, url: imageUrl };
    } catch (error) {
      console.error('Image processing error:', error);
      toast.error('Failed to process image');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Switch camera
  const switchCamera = () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    setIsMirrored(newMode === "user");
    setCameraError(false);
  };

  // Webcam capture
  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    setCurrentStep('crop');
  }, [webcamRef]);

  // Dropzone configuration
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      try {
        const result = await processImage(file);
        setUploadedImage(result.url);
        setCurrentStep('crop');
      } catch (error) {
        console.error('Upload processing error:', error);
      }
    }
  }, [processImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1
  });

  // Handle crop completion
  const onCropComplete = useCallback((crop) => {
    setCompletedCrop(crop);
  }, []);

  // Generate cropped image
  const getCroppedImg = useCallback(() => {
    if (!completedCrop || !imageRef.current || !previewCanvasRef.current) {
      return;
    }

    const image = imageRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio || 1;

    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          console.error('Canvas is empty');
          return;
        }
        const croppedImageUrl = URL.createObjectURL(blob);
        setCroppedImage(croppedImageUrl);
        setCroppedBlob(blob); // Save actual blob for upload
        setCurrentStep('preview');
      },
      'image/jpeg',
      0.9
    );
  }, [completedCrop]);

  // Upload to Supabase
  // In PhotoUpload.js, update the uploadToSupabase function:

  const uploadToSupabase = async () => {
    // Priority: 1. Cropped Blob, 2. Captured Image (Base64), 3. Uploaded Image (URL)
    let uploadBlob = croppedBlob;
    const imageUrl = croppedImage || capturedImage || uploadedImage;

    if (!imageUrl || !player) {
      toast.error('Missing image or player data');
      return;
    }

    setIsProcessing(true);
    setUploadProgress(0);

    try {
      // If we don't have a direct blob yet (e.g. captured image without crop)
      // we need to fetch it one last time. But with the new flow, croppedBlob should be there.
      if (!uploadBlob) {
        const response = await fetch(imageUrl);
        uploadBlob = await response.blob();
      }

      // Sanitize filename: replace spaces and special characters
      const safePlayerName = player.player_name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const safeTeamName = player.team.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const fileName = `${currentEventCode}/${safeTeamName}/${safePlayerName}_${Date.now()}.jpg`;

      // Upload to Supabase Storage with REAL progress tracking
      const { error: storageError } = await supabase.storage
        .from('player-photos')
        .upload(fileName, uploadBlob, {
          contentType: 'image/jpeg',
          upsert: true,
          onUploadProgress: (progress) => {
            const percentage = Math.round((progress.loaded / progress.total) * 100);
            setUploadProgress(percentage);
          }
        });

      if (storageError) {
        console.error('Supabase Storage Error:', storageError);
        throw new Error(`Storage error: ${storageError.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('player-photos')
        .getPublicUrl(fileName);

      // Update database
      const { error: updateError } = await supabase
        .from('event_uploads')
        .update({
          upload_status: true,
          photo_url: publicUrl,
          uploaded_photo_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (updateError) {
        console.error('Database Update Error:', updateError);
        throw new Error(`Database error: ${updateError.message}`);
      }

      setUploadProgress(100);
      toast.success('Photo uploaded successfully!');

      if (onUploadComplete) {
        onUploadComplete(player.id, publicUrl);
      }

      setTimeout(() => {
        resetComponent();
      }, 500);

    } catch (error) {
      console.error('Detailed Upload error:', error);
      toast.error(error.message || 'Failed to upload photo. Please try again.');
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  // Reset component
  const resetComponent = () => {
    setCurrentStep('select');
    setMode(null);
    setCapturedImage(null);
    setUploadedImage(null);
    setCroppedImage(null);
    setCroppedBlob(null);
    setCrop({
      unit: '%',
      width: 80,
      height: 80,
      x: 10,
      y: 10,
      aspect: 1
    });
    setCompletedCrop(null);
    setIsProcessing(false);
    setUploadProgress(0);
    // setShowSuccessModal(false);
  };

  // Progress indicator component
  const ProgressIndicator = () => {
    const steps = [
      { id: 'select', label: 'Method' },
      { id: 'capture', label: 'Capture' },
      { id: 'crop', label: 'Crop' },
      { id: 'preview', label: 'Review' }
    ];

    const currentStepIndex = steps.findIndex(s => s.id === currentStep);

    return (
      <div className="w-full mb-8 relative px-2">
        {/* Progress Line Background */}
        <div className="absolute top-4 left-0 w-full h-0.5 bg-gray-200 -z-10"></div>

        {/* Active Progress Line */}
        <div
          className="absolute top-4 left-0 h-0.5 bg-mjwc-primary transition-all duration-500 -z-10"
          style={{ width: `${(currentStepIndex / (steps.length - 1)) * 100}%` }}
        ></div>

        <div className="flex justify-between items-start">
          {steps.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isActive = index === currentStepIndex;

            return (
              <div key={step.id} className="flex flex-col items-center">
                <div className={`
                  w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold
                  transition-all duration-300
                  ${isCompleted ? 'bg-mjwc-primary border-mjwc-primary text-white' :
                    isActive ? 'bg-white border-mjwc-primary text-mjwc-primary shadow-sm ring-4 ring-blue-50' :
                      'bg-white border-gray-300 text-gray-400'}
                `}>
                  {isCompleted ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                <span className={`
                  text-[10px] sm:text-xs mt-2 uppercase tracking-wider font-bold
                  ${isActive ? 'text-mjwc-primary' : isCompleted ? 'text-gray-700' : 'text-gray-400'}
                `}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render based on current step
  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Progress Indicator */}
      {currentStep !== 'select' && <ProgressIndicator />}

      {/* Step: Select Mode */}
      {currentStep === 'select' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {/* Conditional header based on existing photo */}
          {hasExistingPhoto ? (
            <>
              <div className="mb-4 text-center">
                <div className="w-16 h-16 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Photo Uploaded for {player?.player_name}
                </h3>
                <p className="text-sm text-gray-500">
                  Want to update the photo? Choose an option below
                </p>
              </div>

              {player?.photo_url && (
                <div className="mb-4 flex justify-center">
                  <div className="relative">
                    <img
                      src={player.photo_url}
                      alt={player.player_name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-green-500"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1">
                      <div className="bg-green-500 rounded-full p-1">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Update Photo</strong> for {player?.player_name}
                </p>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                Upload Photo for {player?.player_name}
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Choose how you want to add the photo
              </p>
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => {
                setMode('camera');
                setCurrentStep('capture');
              }}
              className="group relative p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl hover:border-mjwc-primary hover:shadow-lg transition-all duration-300"
            >
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8 text-mjwc-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-900">
                  {hasExistingPhoto ? 'Retake Photo' : 'Take Photo'}
                </span>
                <span className="text-xs text-gray-500 mt-1">Use your camera</span>
              </div>
            </button>

            <button
              onClick={() => {
                setMode('upload');
                setCurrentStep('capture');
              }}
              className="group relative p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl hover:border-mjwc-primary hover:shadow-lg transition-all duration-300"
            >
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8 text-mjwc-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <span className="font-semibold text-gray-900">
                  {hasExistingPhoto ? 'Replace Photo' : 'Upload Photo'}
                </span>
                <span className="text-xs text-gray-500 mt-1">Choose from device</span>
              </div>
            </button>
          </div>

          {/* Photo Requirements - only show for new uploads */}
          {!hasExistingPhoto && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Photo Requirements:</h4>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Clear face photo
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Min 800x800 pixels
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Max 5MB size
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  JPEG/PNG/WebP
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step: Capture (Camera) */}
      {currentStep === 'capture' && mode === 'camera' && (
        <div className="bg-black rounded-xl overflow-hidden">
          <div className="relative" style={{ maxHeight: '60vh' }}> {/* Limit height on mobile */}
            {!cameraError ? (
              <>
                <div className="relative aspect-square max-w-md mx-auto"> {/* Force square aspect ratio */}
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    className="w-full h-full object-cover"
                    style={{
                      transform: isMirrored ? 'scaleX(-1)' : 'none',
                    }}
                    videoConstraints={{
                      width: { ideal: 720 },
                      height: { ideal: 720 },
                      aspectRatio: 1, // Force square aspect ratio
                      facingMode: facingMode
                    }}
                    onUserMediaError={() => setCameraError(true)}
                  />

                  {/* Face Guide Overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="relative w-full h-full flex items-center justify-center">
                      <div className="w-48 h-48 sm:w-64 sm:h-64 border-4 border-white/50 rounded-full"></div>
                      <span className="absolute bottom-4 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
                        Position face in circle
                      </span>
                    </div>
                  </div>

                  {/* Camera Info */}
                  <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {facingMode === "user" ? "Front Camera" : "Back Camera"}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 bg-gray-900 text-white p-6">
                <svg className="w-16 h-16 mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3" />
                </svg>
                <p className="text-lg font-semibold mb-2">Camera Access Denied</p>
                <p className="text-sm text-gray-400 mb-4">Please check your camera permissions</p>
                <button
                  onClick={() => {
                    setCameraError(false);
                    setFacingMode("environment");
                  }}
                  className="px-4 py-2 bg-mjwc-primary text-white rounded-lg hover:bg-mjwc-secondary transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>

          {/* Camera Controls - Compact for mobile */}
          {!cameraError && (
            <div className="bg-gray-900 p-3 sm:p-4">
              <div className="flex justify-between items-center max-w-md mx-auto">
                {/* Back button */}
                <button
                  onClick={() => setCurrentStep('select')}
                  className="p-2 sm:p-3 text-white hover:bg-gray-800 rounded-lg transition-colors"
                  aria-label="Go back"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>

                {/* Switch camera */}
                <button
                  onClick={switchCamera}
                  className="p-2 sm:p-3 text-white hover:bg-gray-800 rounded-lg transition-colors"
                  aria-label="Switch camera"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>

                {/* Capture button - Larger and prominent */}
                <button
                  onClick={capture}
                  className="p-3 sm:p-4 bg-white text-gray-900 rounded-full hover:scale-110 transition-transform shadow-lg"
                  aria-label="Take photo"
                >
                  <svg className="w-7 h-7 sm:w-8 sm:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>

                {/* Mirror toggle */}
                <button
                  onClick={() => setIsMirrored(!isMirrored)}
                  className="p-2 sm:p-3 text-white hover:bg-gray-800 rounded-lg transition-colors"
                  aria-label="Toggle mirror"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </button>

                {/* Spacer for balance */}
                <div className="p-2 sm:p-3 opacity-0 pointer-events-none">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step: Capture (Upload) */}
      {currentStep === 'capture' && mode === 'upload' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <button
            onClick={() => setCurrentStep('select')}
            className="mb-4 flex items-center text-gray-600 hover:text-mjwc-primary transition-colors"
          >
            <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>

          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300
              ${isDragActive
                ? 'border-mjwc-primary bg-blue-50'
                : 'border-gray-300 hover:border-gray-400 bg-gray-50'
              }
              ${isProcessing ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <input {...getInputProps()} />

            {isProcessing ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mjwc-primary mb-4"></div>
                <p className="text-gray-600">Processing image...</p>
              </div>
            ) : (
              <>
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-lg font-semibold text-gray-700 mb-2">
                  {isDragActive ? 'Drop the photo here' : 'Drag & drop your photo'}
                </p>
                <p className="text-sm text-gray-500 mb-4">or</p>
                <button className="px-6 py-2 bg-mjwc-primary text-white rounded-lg hover:bg-mjwc-secondary transition-colors">
                  Browse Files
                </button>
                <p className="text-xs text-gray-400 mt-4">
                  Supported: JPEG, PNG, WebP (Max 5MB)
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Step: Crop */}
      {currentStep === 'crop' && (capturedImage || uploadedImage) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Crop Your Photo</h3>
            <p className="text-sm text-gray-500">Adjust the square to frame the face properly</p>
          </div>

          <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => onCropComplete(c)}
              aspect={1}
              className="max-h-[400px] mx-auto"
            >
              <img
                ref={imageRef}
                src={capturedImage || uploadedImage}
                alt="Crop"
                style={{ maxHeight: '400px', width: 'auto' }}
                onLoad={(e) => {
                  // Set initial crop after image loads
                  const { width, height } = e.currentTarget;
                  const size = Math.min(width, height) * 0.8;
                  const x = (width - size) / 2;
                  const y = (height - size) / 2;

                  const initialCrop = {
                    unit: 'px',
                    width: size,
                    height: size,
                    x: x,
                    y: y,
                    aspect: 1
                  };

                  setCrop(initialCrop);
                  // CRITICAL: Set completedCrop immediately so "Apply Crop" is ready 
                  // even if the user doesn't touch the crop handles
                  setCompletedCrop(initialCrop);
                }}
              />
            </ReactCrop>
          </div>

          {/* Crop Controls */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                setCapturedImage(null);
                setUploadedImage(null);
                setCurrentStep('capture');
              }}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Retake
            </button>
            <button
              onClick={getCroppedImg}
              className="flex-1 px-4 py-2 bg-mjwc-primary text-white rounded-lg hover:bg-mjwc-secondary transition-colors"
            >
              Apply Crop
            </button>
          </div>

          {/* Hidden canvas for cropped image generation */}
          <canvas
            ref={previewCanvasRef}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Step: Preview & Upload */}
      {currentStep === 'preview' && croppedImage && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Review Photo</h3>
            <p className="text-sm text-gray-500">Final preview before uploading</p>
          </div>

          <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4">
            <img
              src={croppedImage}
              alt="Final Preview"
              className="w-full h-auto max-h-[400px] object-contain"
            />

            {/* Success Badge */}
            <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Ready to upload
            </div>
          </div>

          {/* Upload Progress */}
          {uploadProgress > 0 && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-mjwc-primary to-mjwc-secondary transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                setCroppedImage(null);
                setCurrentStep('crop');
              }}
              disabled={isProcessing}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Re-crop
            </button>
            <button
              onClick={uploadToSupabase}
              disabled={isProcessing}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload Photo
                </>
              )}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default PhotoUpload;